<template>
  <div class="pc-goods">
    <header class="pc-goods__hero">
      <img
        class="pc-goods__hero-bg"
        src="@/assets/image/goods/page_bg.webp"
        alt="Goods page background"
        width="1920"
        height="1036"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-goods__kv"
        src="@/assets/image/ticket/header_bg.webp"
        alt="Goods page header background illustration (decorative)"
        width="1798"
        height="1135"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-goods__title-img"
        src="@/assets/image/goods/title.webp"
        alt="Goods page title graphic"
        width="746"
        height="66"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-goods__deco pc-goods__deco--left"
        src="@/assets/image/goods/nav_icon_left.webp"
        alt="Decorative circular graphic (left)"
        width="381"
        height="381"
        decoding="async"
        loading="lazy"
      />
      <img
        class="pc-goods__deco pc-goods__deco--right"
        src="@/assets/image/goods/nav_icon_right.webp"
        alt="Decorative star graphic (right)"
        width="319"
        height="319"
        decoding="async"
        loading="lazy"
      />

      <div class="pc-goods__marquee">
        <div class="pc-goods__marquee-view">
          <div class="pc-goods__marquee-track">
            <div
              v-for="dup in 2"
              :key="'pc-goods-m-' + dup"
              class="pc-goods__marquee-chunk"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="item in 10"
                :key="`pc-goods-m-${dup}-${item}`"
                class="pc-goods__marquee-img"
                src="@/assets/image/index/banner_footer_title.webp"
                width="314"
                height="30"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="pc-goods__list"
        :class="{ 'pc-goods__list--empty': !goodsLoading && !goodsList.length }"
      >
        <div
          v-if="goodsLoading"
          class="pc-goods__list-loading"
          role="status"
          aria-live="polite"
        >
          <span class="pc-goods__list-loading-spinner" aria-hidden="true" />
        </div>
        <template v-else-if="goodsList.length">
          <div
            v-for="(item, index) in paginatedList"
            :key="'goods-item-' + listSliceStart + index"
            class="pc-goods__list-item"
            role="button"
            tabindex="0"
            @click="openGoodsDetail(item)"
            @keydown.enter.prevent="openGoodsDetail(item)"
            @keydown.space.prevent="openGoodsDetail(item)"
          >
            <div class="pc-goods__list-item-media">
              <div
                v-if="isGoodsImageLoading(item, listSliceStart + index)"
                class="pc-goods__list-item-img-loading"
                aria-hidden="true"
              >
                <span class="pc-goods__list-item-img-spinner" />
              </div>
              <img
                class="pc-goods__list-item-img"
                :class="{ 'is-loading': isGoodsImageLoading(item, listSliceStart + index) }"
                :src="item.img[0]"
                width="280"
                height="260"
                :alt="'Merchandise product image — ' + goodsTitlePlain(item)"
                decoding="async"
                loading="lazy"
                @load="onGoodsImageLoad(item, listSliceStart + index)"
                @error="onGoodsImageError(item, listSliceStart + index)"
              />
            </div>

            <div class="pc-goods__list-item-add-icon">+</div>
            <div class="pc-goods__list-item-content">
              <div class="pc-goods__list-item-info">
                <div
                  v-for="(line, lineIndex) in item.title"
                  :key="lineIndex"
                  class="pc-goods__list-item-info-msg"
                >
                  {{ line }}
                </div>
              </div>
              <div
                class="pc-goods__list-item-price"
                :class="{
                  'pc-goods__list-item-price--plain': !item.priceIsNumeric,
                }"
              >
                <template v-if="item.priceIsNumeric">
                  <span class="label">{{ goodsPageCopy.priceLabel }}</span>
                  <span
                    >{{ item.price }}円
                    <span>{{ goodsPageCopy.taxNote }}</span></span
                  >
                </template>
                <span v-else class="pc-goods__list-item-price-plain">{{
                  item.price
                }}</span>
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          class="pc-goods__list-empty"
          role="status"
          aria-live="polite"
        >
          <p class="pc-goods__list-empty-title">{{ goodsPageCopy.emptyTitle }}</p>
          <p class="pc-goods__list-empty-desc">
            {{ goodsPageCopy.emptyDesc }}
          </p>
        </div>
      </div>

      <nav
        v-if="!goodsLoading && goodsList.length"
        class="pc-goods__pagination"
        :aria-label="goodsPageCopy.paginationAriaLabel"
      >
        <button
          type="button"
          class="pc-goods__pagination-nav"
          :disabled="currentPage <= 1"
          :aria-label="goodsPageCopy.ariaPrevPage"
          @click="goToPage(currentPage - 1)"
        >
          <span class="pc-goods__pagination-chevron" aria-hidden="true" />
        </button>
        <ul class="pc-goods__pagination-pages">
          <li v-for="p in totalPages" :key="'goods-page-' + p">
            <button
              type="button"
              class="pc-goods__pagination-page"
              :class="{ 'is-active': currentPage === p }"
              :aria-current="currentPage === p ? 'page' : undefined"
              :aria-label="goodsPageCopy.ariaPagePrefix + p"
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
          </li>
        </ul>
        <button
          type="button"
          class="pc-goods__pagination-nav pc-goods__pagination-nav--next"
          :disabled="currentPage >= totalPages"
          :aria-label="goodsPageCopy.ariaNextPage"
          @click="goToPage(currentPage + 1)"
        >
          <span class="pc-goods__pagination-chevron" aria-hidden="true" />
        </button>
      </nav>
    </header>

    <PcGoodsDetailModal
      v-if="goodsDetailVisible"
      :goods-list="goodsList"
      :active-index="goodsDetailIndex"
      :can-prev="goodsDetailCanPrev"
      :can-next="goodsDetailCanNext"
      @update:visible="onGoodsDetailVisible"
      @prev="goGoodsDetailPrev"
      @next="goGoodsDetailNext"
      @change-index="onGoodsDetailSwiperIndex"
    />
  </div>
</template>

<script>
import PcGoodsDetailModal from "./detail.vue";
import goodsContent from "@/views/data/goods-content.json";
import { fetchGoodsList, parseContentListResponse } from "@/api/goods";
import {
  hydrateGoodsListFromContent,
  mapGoodsRowToItem,
} from "@/utils/goods-list-api";

const GOODS_PAGE_SIZE = 16;

export default {
  name: "PcGoodsPage",

  components: {
    PcGoodsDetailModal,
  },

  data() {
    return {
      /**
       * 列表页文案（维护 goods-content.json → goodsIndex）
       */
      goodsPageCopy: goodsContent.goodsIndex,
      /** 接口 goods + goods-content.json 兜底 */
      goodsList: [],
      goodsLoading: true,
      currentPage: 1,
      goodsDetailVisible: false,
      goodsDetailIndex: -1,
      /** 打开详情弹窗前窗口滚动位置（弹层会给 html 加 overflow:hidden，关闭后需还原） */
      goodsDetailScrollRestoreY: 0,
      /** 列表首图加载状态：key -> loading | loaded | error */
      goodsImageLoadStatus: {},
    };
  },

  computed: {
    goodsDetailCanPrev() {
      return this.goodsDetailIndex > 0;
    },
    goodsDetailCanNext() {
      return (
        this.goodsDetailIndex >= 0 &&
        this.goodsDetailIndex < this.goodsList.length - 1
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.goodsList.length / GOODS_PAGE_SIZE));
    },
    listSliceStart() {
      return (this.currentPage - 1) * GOODS_PAGE_SIZE;
    },
    paginatedList() {
      return this.goodsList.slice(
        this.listSliceStart,
        this.listSliceStart + GOODS_PAGE_SIZE
      );
    },
  },

  watch: {
    goodsList() {
      this.clampCurrentPage();
    },
    totalPages() {
      this.clampCurrentPage();
    },
    paginatedList: {
      immediate: true,
      handler() {
        this.ensureVisibleGoodsImageLoadStatus();
      },
    },
  },

  created() {
    this.loadGoodsList();
  },

  methods: {
    async loadGoodsList() {
      this.goodsLoading = true;
      try {
        const res = await fetchGoodsList({ index: 1, size: 200 });
        const { rows } = parseContentListResponse(res);
        this.goodsList = rows.map(mapGoodsRowToItem);
      } catch (e) {
        console.error("[goods] fetchGoodsList", e);
        this.goodsList = hydrateGoodsListFromContent(goodsContent.goodsList);
      } finally {
        this.goodsLoading = false;
      }
    },
    goodsImageStatusKey(item, absoluteIndex) {
      const src = item && Array.isArray(item.img) && item.img[0] ? item.img[0] : "";
      return `${absoluteIndex}:${src}`;
    },
    ensureVisibleGoodsImageLoadStatus() {
      this.paginatedList.forEach((item, index) => {
        const absoluteIndex = this.listSliceStart + index;
        const key = this.goodsImageStatusKey(item, absoluteIndex);
        if (!this.goodsImageLoadStatus[key]) {
          this.$set(this.goodsImageLoadStatus, key, "loading");
        }
      });
    },
    isGoodsImageLoading(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      const status = this.goodsImageLoadStatus[key];
      return !status || status === "loading";
    },
    onGoodsImageLoad(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      this.$set(this.goodsImageLoadStatus, key, "loaded");
    },
    onGoodsImageError(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      this.$set(this.goodsImageLoadStatus, key, "error");
    },
    goodsTitlePlain(item) {
      if (!item || !Array.isArray(item.title)) return "";
      return item.title
        .map((t) => String(t).trim())
        .filter(Boolean)
        .join(" ");
    },
    clampCurrentPage() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
      if (this.currentPage < 1) {
        this.currentPage = 1;
      }
    },
    goToPage(page) {
      const p = Number(page);
      if (p < 1 || p > this.totalPages || p === this.currentPage) return;
      this.currentPage = p;
      this.scrollGoodsPageTop();
    },
    scrollGoodsPageTop() {
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? "auto"
        : "smooth";
      window.scrollTo({ top: 0, left: 0, behavior });
    },
    openGoodsDetail(item) {
      if (typeof window !== "undefined") {
        this.goodsDetailScrollRestoreY =
          window.scrollY ||
          window.pageYOffset ||
          (typeof document !== "undefined"
            ? document.documentElement.scrollTop
            : 0) ||
          0;
      }
      const i = this.goodsList.indexOf(item);
      this.goodsDetailIndex = i >= 0 ? i : 0;
      this.goodsDetailVisible = true;
    },
    onGoodsDetailVisible(open) {
      this.goodsDetailVisible = open;
      if (!open && typeof window !== "undefined") {
        const y = this.goodsDetailScrollRestoreY;
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, left: 0, behavior: "auto" });
          });
        });
      }
    },
    goGoodsDetailPrev() {
      if (!this.goodsDetailCanPrev) return;
      this.goodsDetailIndex -= 1;
    },
    goGoodsDetailNext() {
      if (!this.goodsDetailCanNext) return;
      this.goodsDetailIndex += 1;
    },
    onGoodsDetailSwiperIndex(i) {
      if (!this.goodsDetailVisible) return;
      const n = Number(i);
      if (n < 0 || n >= this.goodsList.length) return;
      this.goodsDetailIndex = n;
    },
  },
};
</script>

<style lang="scss" scoped>
.pc-goods {
  background-color: #3c3068;
  box-sizing: border-box;
  text-align: left;
  border-bottom: 2px solid #9f77bb;

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  &__hero {
    position: relative;
    width: 100%;
    padding-top: 500px;
    padding-bottom: 134px;
    min-height: 1566px;
    box-sizing: border-box;
    background-image: url("@/assets/image/ticket/nav_bg.webp");
    background-size: 100% auto;
    background-repeat: no-repeat;

    .pc-goods__hero-bg {
      position: absolute;
      top: 0;
      left: 50%;
      width: 100vw;
      max-width: none;
      height: 1100px;
      transform: translateX(-50%);
      object-fit: cover;
      object-position: center;
    }

    .pc-goods__kv {
      position: absolute;
      top: 431px;
      left: 50%;
      width: 100vw;
      max-width: none;
      height: 1135px;
      transform: translateX(-50%);
      object-fit: cover;
      object-position: center;
      z-index: -1;
    }

    .pc-goods__title-img {
      position: absolute;
      top: 180px;
      left: 588px;
      width: 746px;
      object-fit: contain;
      object-position: center;
    }

    .pc-goods__deco {
      position: absolute;
      object-fit: contain;
      object-position: center;
      transform-origin: center center;
      will-change: transform;
      animation: pc-goods-deco-beat 1.35s infinite;

      &--left {
        top: 88px;
        left: 212px;
        width: 300px;
      }

      &--right {
        top: 107px;
        right: 290px;
        width: 232px;
      }
    }

    .pc-goods__marquee {
      position: absolute;
      top: 306px;
      left: 0;
      width: 100%;
      height: 134px;
      background-image: url("@/assets/image/index/banner_footer_bg.webp");
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      z-index: 10;

      .pc-goods__marquee-view {
        margin-top: 45px;
        overflow: hidden;
        height: 44px;
        display: flex;
        align-items: center;
        background-color: #191616;
        border-top: 1px solid #fff590;
        border-bottom: 1px solid #fff590;

        .pc-goods__marquee-track {
          display: flex;
          width: max-content;
          flex-shrink: 0;
          align-items: center;
          animation: pc-goods-marquee 45s linear infinite;
          will-change: transform;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
            will-change: auto;
          }

          .pc-goods__marquee-chunk {
            display: flex;
            flex-shrink: 0;
            align-items: center;

            .pc-goods__marquee-img {
              flex-shrink: 0;
              margin-right: 30px;
              width: 314px;
              height: 30px;
              object-fit: contain;
              object-position: center;
            }
          }
        }
      }
    }
  }

  .pc-goods__list {
    position: relative;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    padding: 0 320px;

    &-loading {
      display: flex;
      flex: 1 1 100%;
      justify-content: center;
      align-items: center;
      min-height: 360px;
      box-sizing: border-box;
    }

    &-loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 243, 128, 0.25);
      border-top-color: #fff380;
      border-radius: 50%;
      animation: pc-goods-list-loading-spin 0.75s linear infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        border-color: #fff380;
        opacity: 0.85;
      }
    }

    &--empty {
      flex-wrap: nowrap;
      justify-content: center;
      align-items: center;
      min-height: 360px;
      padding-top: 48px;
      padding-bottom: 72px;
      box-sizing: border-box;
    }

    &-empty {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      padding: 40px 32px 44px;
      text-align: center;
      background-color: #191616;
      border: 2px solid #fff380;
      border-radius: 8px;
      box-sizing: border-box;
    }

    &-empty-title {
      margin: 0 0 16px;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: 0.12em;
      color: #fff380;
    }

    &-empty-desc {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      line-height: 1.65;
      color: rgba(255, 255, 255, 0.92);
    }

    &-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 18px;
      width: 280px;
      object-fit: contain;
      object-position: center;
      border-radius: 8px;
      border: 2px solid #fff380;
      text-align: center;
      background-color: #191616;
      cursor: pointer;
      box-sizing: border-box;
      transition: box-shadow 0.28s ease, border-color 0.28s ease;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }

      &:hover,
      &:focus-visible {
        border-color: #fff9b0;
        box-shadow: 0 16px 32px rgba(0, 0, 0, 0.5),
          0 0 0 1px rgba(255, 243, 128, 0.4), 0 0 28px rgba(255, 243, 128, 0.28);
        z-index: 1;
      }

      &:focus-visible {
        outline: none;
      }

      &:active {
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45),
          0 0 18px rgba(255, 243, 128, 0.2);
        transition-duration: 0.12s;
      }

      &:hover .pc-goods__list-item-img,
      &:focus-visible .pc-goods__list-item-img {
        transform: scale(1.08);
      }

      &:active .pc-goods__list-item-img {
        transform: scale(1.04);
        transition-duration: 0.12s;
      }

      @media (prefers-reduced-motion: reduce) {
        &:hover,
        &:active,
        &:focus-visible {
          box-shadow: none;
        }

        &:hover .pc-goods__list-item-img,
        &:focus-visible .pc-goods__list-item-img,
        &:active .pc-goods__list-item-img {
          transform: none;
        }
      }

      &-media {
        position: relative;
        flex-shrink: 0;
        width: 100%;
        max-width: 280px;
        height: 276px;
        overflow: hidden;
        border-radius: 6px 6px 0 0;
        background: rgba(255, 255, 255, 0.08);
      }

      &-img-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(
          180deg,
          rgba(25, 22, 22, 0.7) 0%,
          rgba(25, 22, 22, 0.42) 100%
        );
        z-index: 1;
        pointer-events: none;
      }

      &-img-spinner {
        width: 28px;
        height: 28px;
        border: 3px solid rgba(255, 243, 128, 0.3);
        border-top-color: #fff380;
        border-radius: 50%;
        animation: pc-goods-img-spin 0.9s linear infinite;
      }

      &-img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
        transform-origin: center center;
        transition: transform 0.28s cubic-bezier(0.25, 0.82, 0.25, 1),
          opacity 0.2s ease;

        &.is-loading {
          opacity: 0;
        }

        @media (prefers-reduced-motion: reduce) {
          transition: none;
        }
      }

      &-add-icon {
        position: absolute;
        top: 236px;
        right: 3px;
        width: 40px;
        height: 40px;
        background-color: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        text-align: center;
        line-height: 40px;
        font-size: 30px;
        color: #fff380;
      }

      &-content {
        width: 100%;
        align-self: stretch;
        box-sizing: border-box;
        background-color: #fff380;
      }

      &-info {
        padding: 23px 20px;
        color: #191616;
        font-weight: 700;
        height: 90px;

        &-msg {
          font-size: 12px;
          font-weight: 700;
          text-align: left;
        }
      }
      &-price {
        padding: 0 20px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-weight: 700 !important;
        background-color: #fff380;

        .label {
          font-size: 14px;
        }

        span {
          font-size: 16px;

          span {
            font-size: 10px;
          }
        }

        &--plain {
          justify-content: flex-end;
        }
      }

      &-price-plain {
        width: 100%;
        text-align: right;
        font-size: 16px;
        line-height: 1.35;
        font-weight: 700;
        word-break: break-word;
        overflow-wrap: anywhere;
        color: #191616;
      }
    }
  }

  &__pagination {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 40px;
    margin-top: 48px;
    padding: 24px 320px 48px;
    box-sizing: border-box;
  }

  &__pagination-nav {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.12);
    cursor: pointer;
    transition: background-color 0.2s ease, opacity 0.2s ease;

    &:hover:not(:disabled) {
      background-color: rgba(255, 255, 255, 0.2);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &--next .pc-goods__pagination-chevron {
      transform: rotate(225deg);
    }
  }

  &__pagination-chevron {
    display: block;
    width: 10px;
    height: 10px;
    margin-left: 2px;
    border-left: 3px solid #fff380;
    border-bottom: 3px solid #fff380;
    transform: rotate(45deg);
  }

  &__pagination-pages {
    display: flex;
    align-items: flex-start;
    gap: 36px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__pagination-page {
    position: relative;
    min-width: 1.25em;
    padding: 0 4px 10px;
    border: none;
    background: none;
    font-size: 18px;
    line-height: 1;
    color: #fff;
    cursor: pointer;
    font-weight: 700;
    transition: color 0.2s ease, opacity 0.2s ease;

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      bottom: 0;
      width: 0;
      height: 2px;
      border-radius: 2px;
      background-color: #fff380;
      transform: translateX(-50%);
      transition: width 0.2s ease;
    }

    &:hover:not(.is-active) {
      opacity: 0.85;
    }

    &.is-active {
      &::after {
        width: 12px;
      }
    }
  }
}

@keyframes pc-goods-deco-beat {
  0%,
  42% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  52% {
    transform: scale(0.9);
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.6, 0.45);
  }
  58% {
    transform: scale(0.86);
    animation-timing-function: cubic-bezier(0.33, 1.15, 0.52, 1);
  }
  70% {
    transform: scale(1.08);
    animation-timing-function: cubic-bezier(0.22, 0.85, 0.38, 1);
  }
  80% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes pc-goods-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes pc-goods-img-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pc-goods-list-loading-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
